import { useState } from "react";
import Papa from "papaparse";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, FileUp } from "lucide-react";

const REQUIRED_HEADERS = [
  "doc_id","domain","title","version","status","requires_founder_review","trigger_tags","sort_order","body_markdown",
];

type Row = Record<string, string>;

function toBool(v: string): boolean {
  const s = (v || "").trim().toLowerCase();
  return s === "true" || s === "t" || s === "yes" || s === "y" || s === "1";
}

export function ManualCsvImport({ onDone }: { onDone?: () => void }) {
  const { user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [report, setReport] = useState<{ inserted: number; updated: number; failed: number; errors: string[] } | null>(null);

  function parseFile(file: File) {
    setError(null);
    setReport(null);
    Papa.parse<Row>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const headers = res.meta.fields || [];
        const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length) {
          setError(`Missing required headers: ${missing.join(", ")}`);
          setRows([]);
          return;
        }
        setRows(res.data.filter((r) => r.doc_id?.trim()));
      },
      error: (err) => setError(err.message),
    });
  }

  async function runImport() {
    if (!rows.length) return;
    setRunning(true);
    setProgress(0);
    let inserted = 0, updated = 0, failed = 0;
    const errors: string[] = [];

    // Snapshot existing doc_ids to differentiate insert vs update
    const { data: existing } = await supabase.from("sop_documents").select("doc_id");
    const existingSet = new Set((existing || []).map((r) => r.doc_id));

    const batchSize = 25;
    for (let i = 0; i < rows.length; i += batchSize) {
      const batch = rows.slice(i, i + batchSize).map((r) => {
        let sourceRefs: any = null;
        const rawRefs = (r.source_refs || "").trim();
        if (rawRefs) {
          try {
            const parsed = JSON.parse(rawRefs);
            if (Array.isArray(parsed)) sourceRefs = parsed;
          } catch {
            // leave null on parse failure
          }
        }
        return {
          doc_id: r.doc_id.trim(),
          domain: (r.domain || "").trim(),
          title: (r.title || "").trim(),
          version: (r.version || "").trim(),
          status: (r.status || "draft").trim(),
          requires_founder_review: toBool(r.requires_founder_review),
          trigger_tags: r.trigger_tags || "",
          sort_order: r.sort_order ? parseInt(r.sort_order, 10) || 0 : 0,
          body_markdown: r.body_markdown || "",
          collection: (r.collection || "sop").trim() || "sop",
          source_refs: sourceRefs,
          updated_by: user?.email || null,
        };
      });

      const { error, data } = await supabase
        .from("sop_documents")
        .upsert(batch, { onConflict: "doc_id" })
        .select("doc_id");

      if (error) {
        failed += batch.length;
        errors.push(error.message);
      } else {
        for (const row of batch) {
          if (existingSet.has(row.doc_id)) updated++;
          else { inserted++; existingSet.add(row.doc_id); }
        }
      }
      setProgress(Math.min(100, Math.round(((i + batch.length) / rows.length) * 100)));
    }

    setReport({ inserted, updated, failed, errors: errors.slice(0, 5) });
    setRunning(false);
    // Refresh the semantic search index in the background.
    supabase.functions.invoke("embed-docs", { body: {} }).catch(() => {});
    supabase.functions.invoke("link-audit").catch(() => {});
    onDone?.();
  }

  return (
    <Card className="p-4 md:p-6 space-y-4">
      <h2 className="text-lg font-serif font-semibold">CSV Import</h2>
      <p className="text-xs text-muted-foreground">
        Required headers: {REQUIRED_HEADERS.join(", ")}. Rows are upserted by <code>doc_id</code>.
      </p>

      <label
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false);
          const f = e.dataTransfer.files?.[0]; if (f) parseFile(f);
        }}
        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg h-32 cursor-pointer transition ${dragOver ? "bg-muted border-primary" : "hover:bg-muted/40"}`}
      >
        <FileUp className="h-6 w-6 text-muted-foreground mb-2" />
        <div className="text-sm text-muted-foreground">Drop CSV here or click to select</div>
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) parseFile(f); }} />
      </label>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {rows.length > 0 && !report && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">Parsed <b>{rows.length}</b> rows ready to upsert.</div>
          <Button onClick={runImport} disabled={running}>
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Import
          </Button>
        </div>
      )}

      {running && <Progress value={progress} />}

      {report && (
        <Alert>
          <AlertDescription>
            <div className="space-y-1 text-sm">
              <div>Inserted: <b>{report.inserted}</b></div>
              <div>Updated: <b>{report.updated}</b></div>
              <div>Failed: <b>{report.failed}</b></div>
              {report.errors.length > 0 && (
                <ul className="list-disc pl-5 text-destructive text-xs">
                  {report.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}
    </Card>
  );
}
